import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import {
  DialogCloseResult,
  DialogService,
} from '@progress/kendo-angular-dialog';
import {
  GridDataResult,
  GridModule,
  PageChangeEvent,
  PagerSettings,
} from '@progress/kendo-angular-grid';
import {
  KENDO_RADIOBUTTON,
  SwitchModule,
} from '@progress/kendo-angular-inputs';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  EmailSubscriptionConfiguration,
  EmailsExclusionListItem,
  EmailSyncService,
  OUTLOOK_SYNC_FREQUENCY,
  OutlookSyncEntityType,
  OutlookSyncHistoryEntry,
  OutlookSyncType as OutlookSyncApiType,
  UserSyncConfiguration,
  UserSyncConfigurationService,
} from '@maximizer/outlook-sync/data-access';
import { EMPTY } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { HistoryGridComponent } from '../../components/history-grid/history-grid.component';
import { OutlookSyncFormGroup } from '../../../form.model';
import { loadSyncHistory } from '../../utils/history-mapper.util';

interface EmailSubscriptionView {
  maximizerKey: string;
  contactName: string;
  companyName: string;
  emailAddresses: string[];
  createdAt?: string;
}

interface ExclusionView {
  maximizerKey: string;
  maximizerEntityType: OutlookSyncEntityType;
  contactName: string;
  companyName: string;
  emailAddresses: string[];
  createdAt?: string;
}

const SUBSCRIPTION_PAGE_SIZES = [5, 10, 20] as const;

const EMAIL_TAB_PAGER: PagerSettings = {
  buttonCount: 5,
  info: true,
  type: 'numeric',
  pageSizes: [...SUBSCRIPTION_PAGE_SIZES],
};

@Component({
  selector: 'maximizer-outlook-email-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    GridModule,
    SwitchModule,
    TooltipModule,
    TranslatePipe,
    HistoryGridComponent,
    KENDO_BUTTON,
    KENDO_RADIOBUTTON,
  ],
  templateUrl: './outlook-email-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OutlookEmailTabComponent {
  private readonly userSyncConfig = inject(UserSyncConfigurationService);
  private readonly emailSync = inject(EmailSyncService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly translate = inject(TranslateService);

  configuration = input.required<UserSyncConfiguration | null>();
  form = input.required<OutlookSyncFormGroup>();

  protected readonly history = signal<OutlookSyncHistoryEntry[]>([]);
  protected readonly historyLoading = signal(false);

  protected readonly syncFrequency = OUTLOOK_SYNC_FREQUENCY.emails;
  protected readonly emailSubscriptionLimit = 5000;

  protected readonly emailSubscriptions = signal<EmailSubscriptionView[]>([]);
  protected readonly totalEmailSubscriptions = signal(0);
  protected readonly emailLoading = signal(false);
  protected readonly emailSkip = signal(0);
  protected readonly emailPageSize = signal<number>(SUBSCRIPTION_PAGE_SIZES[0]);
  protected readonly emailPager: PagerSettings = EMAIL_TAB_PAGER;

  protected readonly emailGridData = computed<GridDataResult>(() => ({
    data: this.emailSubscriptions(),
    total: this.totalEmailSubscriptions(),
  }));

  protected readonly exclusions = signal<ExclusionView[]>([]);
  protected readonly totalExclusions = signal(0);
  protected readonly exclusionLoading = signal(false);
  protected readonly exclusionSkip = signal(0);
  protected readonly exclusionPageSize = signal<number>(
    SUBSCRIPTION_PAGE_SIZES[0],
  );
  protected readonly exclusionPager: PagerSettings = EMAIL_TAB_PAGER;
  protected readonly exclusionGridData = computed<GridDataResult>(() => {
    const all = this.exclusions();
    const skip = this.exclusionSkip();
    const take = this.exclusionPageSize();
    return {
      data: all.slice(skip, skip + take),
      total: this.totalExclusions(),
    };
  });

  private currentConfigurationId: string | null = null;

  constructor() {
    effect(() => {
      this.configuration();
      this.handleConfigurationChange();
    });
  }

  protected refreshHistory(): void {
    const configurationId = this.configuration()?.id ?? null;
    loadSyncHistory({
      service: this.userSyncConfig,
      destroyRef: this.destroyRef,
      syncType: OutlookSyncApiType.Emails,
      idPrefix: 'email-history',
      errorMessage: 'Failed to load email sync history',
      configurationId,
      isCurrent: () => this.currentConfigurationId === configurationId,
      history: this.history,
      loading: this.historyLoading,
    });
  }

  protected refreshEmailSubscriptions(resetPage = false): void {
    const configurationId = this.configuration()?.id;
    if (!configurationId) {
      this.emailSubscriptions.set([]);
      this.totalEmailSubscriptions.set(0);
      return;
    }

    if (resetPage) {
      this.emailSkip.set(0);
    }

    this.loadEmailSubscriptions(
      configurationId,
      this.emailSkip(),
      this.emailPageSize(),
    );
  }

  protected onEmailPageChange(event: PageChangeEvent): void {
    const configurationId = this.configuration()?.id;
    if (!configurationId) {
      return;
    }

    this.emailSkip.set(event.skip);
    this.emailPageSize.set(event.take);
    this.loadEmailSubscriptions(configurationId, event.skip, event.take);
  }

  protected removeSubscription(maximizerKey: string): void {
    const configurationId = this.configuration()?.id;
    if (!configurationId || !maximizerKey) {
      return;
    }

    this.emailLoading.set(true);
    this.emailSync
      .deleteEmailSubscription(configurationId, maximizerKey)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.emailLoading.set(false)),
        tap((result) => {
          if (!result) {
            return;
          }

          const pageSize = this.emailPageSize();
          const totalAfterRemoval = Math.max(
            this.totalEmailSubscriptions() - 1,
            0,
          );
          const nextSkip = this.clampPageSkip(
            this.emailSkip,
            totalAfterRemoval,
            pageSize,
          );

          this.loadEmailSubscriptions(configurationId, nextSkip, pageSize);
        }),
        catchError((error) => {
          console.error('Failed to delete email subscription', error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  protected refreshExclusions(): void {
    const configurationId = this.configuration()?.id;
    if (!configurationId) {
      this.exclusions.set([]);
      this.totalExclusions.set(0);
      return;
    }

    this.exclusionLoading.set(true);
    this.emailSync
      .getEmailExclusionList(configurationId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.exclusionLoading.set(false)),
        tap((response) => {
          if (this.currentConfigurationId !== configurationId) {
            return;
          }
          const mapped = this.mapExclusions(response?.items ?? []);
          this.exclusions.set(mapped);
          this.totalExclusions.set(response?.totalExcluded ?? mapped.length);
          this.exclusionSkip.set(0);
        }),
        catchError((error) => {
          console.error('Failed to load email exclusion list', error);
          if (this.currentConfigurationId === configurationId) {
            this.exclusions.set([]);
            this.totalExclusions.set(0);
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  protected onExclusionPageChange(event: PageChangeEvent): void {
    this.exclusionSkip.set(event.skip);
    this.exclusionPageSize.set(event.take);
  }

  protected requestRemoveExclusion(item: ExclusionView): void {
    const cancelText = this.translate.instant(
      'outlookSync.dialogs.close',
    );
    const confirmText = this.translate.instant(
      'outlookSync.exclusions.remove',
    );

    const dialog = this.dialogService.open({
      title: this.translate.instant(
        'outlookSync.exclusions.confirm.title',
      ),
      content: this.translate.instant(
        'outlookSync.exclusions.confirm.body',
        { name: item.contactName },
      ),
      actions: [
        { text: cancelText },
        { text: confirmText, themeColor: 'primary' },
      ],
      width: 440,
      minWidth: 280,
    });

    dialog.result
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result instanceof DialogCloseResult) {
          return;
        }
        if (result.text === confirmText) {
          this.executeRemoveExclusion(item);
        }
      });
  }

  private executeRemoveExclusion(item: ExclusionView): void {
    const configurationId = this.configuration()?.id;
    if (!configurationId) {
      return;
    }

    this.exclusionLoading.set(true);
    this.emailSync
      .removeFromEmailExclusionList(
        configurationId,
        item.maximizerKey,
        item.maximizerEntityType,
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.exclusionLoading.set(false)),
        tap((success) => {
          if (!success) {
            return;
          }
          const remaining = this.exclusions().filter(
            (entry) =>
              !(
                entry.maximizerKey === item.maximizerKey &&
                entry.maximizerEntityType === item.maximizerEntityType
              ),
          );
          this.exclusions.set(remaining);
          this.totalExclusions.set(remaining.length);

          this.clampPageSkip(
            this.exclusionSkip,
            remaining.length,
            this.exclusionPageSize(),
          );
        }),
        catchError((error) => {
          console.error('Failed to remove from email exclusion list', error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  protected get emailSubscriptionModeDisabled(): boolean {
    return !this.form().controls.emailsSyncEnabled.value;
  }

  protected selectEmailSubscriptionMode(mode: 'auto' | 'manual'): void {
    if (this.emailSubscriptionModeDisabled) {
      return;
    }
    const next = mode === 'auto';
    const form = this.form();
    const control = form.controls.emailsSyncSyncEverything;
    if (control.value === next) {
      return;
    }
    control.setValue(next);
    control.markAsDirty();
    control.markAsTouched();
    form.markAsDirty();
  }

  private handleConfigurationChange(): void {
    const configurationId = this.configuration()?.id ?? null;
    if (!configurationId) {
      this.currentConfigurationId = null;
      this.history.set([]);
      this.emailSubscriptions.set([]);
      this.totalEmailSubscriptions.set(0);
      this.exclusions.set([]);
      this.totalExclusions.set(0);
      return;
    }

    if (configurationId === this.currentConfigurationId) {
      return;
    }

    this.currentConfigurationId = configurationId;
    this.refreshHistory();
    this.refreshEmailSubscriptions(true);
    this.refreshExclusions();
  }

  private loadEmailSubscriptions(
    configurationId: string,
    skip: number,
    take: number,
  ): void {
    this.emailLoading.set(true);
    this.emailSync
      .getEmailSubscriptions(configurationId, {
        offset: skip,
        pageSize: take,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.emailLoading.set(false)),
        tap((response) => {
          if (this.currentConfigurationId !== configurationId) {
            return;
          }
          const mapped = this.mapEmailSubscriptions(response?.data ?? []);
          this.emailSubscriptions.set(mapped);
          this.totalEmailSubscriptions.set(response?.count ?? mapped.length);
        }),
        catchError((error) => {
          console.error('Failed to load email subscriptions', error);
          if (this.currentConfigurationId === configurationId) {
            this.emailSubscriptions.set([]);
            this.totalEmailSubscriptions.set(0);
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private clampPageSkip(
    skipSignal: WritableSignal<number>,
    total: number,
    pageSize: number,
  ): number {
    let skip = skipSignal();
    if (total <= skip && skip > 0) {
      skip = Math.max(0, skip - pageSize);
      skipSignal.set(skip);
    }
    return skip;
  }

  private mapEmailSubscriptions(
    data: EmailSubscriptionConfiguration[],
  ): EmailSubscriptionView[] {
    return data.map((item) => ({
      maximizerKey: item.maximizerKey,
      contactName: item.contactName ?? '',
      companyName: item.companyName ?? '',
      emailAddresses: item.emailAddresses ?? [],
      createdAt: item.createdAt ?? undefined,
    }));
  }

  private mapExclusions(data: EmailsExclusionListItem[]): ExclusionView[] {
    return data.map((item) => ({
      maximizerKey: item.maximizerKey,
      maximizerEntityType: item.maximizerEntityType,
      contactName: item.contactName ?? '',
      companyName: item.companyName ?? '',
      emailAddresses: item.emailAddresses ?? [],
      createdAt: item.createdAt ?? undefined,
    }));
  }
}
